import { type AdminHostPoolWithType, useAllHostPoolsQuery } from "@/admin/host_pools/hostPoolsQuery.ts";
import { WPopover } from "@/components/ActionMenu.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LeftRight } from "@/components/LeftRight.tsx";
import { CountryFlag } from "@/modules/location/CountryFlag.tsx";
import { getPreferredLocation, type LocationWithEnabled } from "@/modules/location/location.ts";
import { type CountryInfo, getIso3CountryInfo } from "@/modules/location/locationCountry.ts";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { type ReactNode, useState } from "react";
import { Label, MenuTrigger } from "react-aria-components";

export type HostScope =
    | { type: "country"; value: CountryInfo }
    | { type: "location"; value: LocationWithEnabled }
    | { type: "pool"; value: AdminHostPoolWithType };

export function AdminCountryLocationPoolSelect({ onChange }: { onChange: (scope: HostScope) => void }) {
    const locations = usePricingLocations();
    const location = getPreferredLocation(locations);
    const countryCode = location.country_code;
    const hostPools = useAllHostPoolsQuery();
    const [selectedName, setSelectedName] = useState<ReactNode>(getIso3CountryInfo(countryCode)?.name);
    const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode);

    const countryCodes = [...new Set(locations.map((loc) => loc.country_code))];

    const treeStructure = countryCodes.map((countryCode) => ({
        country: getIso3CountryInfo(countryCode)!,
        locations: locations
            .filter((location) => location.country_code === countryCode)
            .map((location) => ({
                location,
                pools: [...hostPools.values()].filter((p) => p.location === location.slug),
            })),
    }));

    function onChangeScope(scope: HostScope, name: string, countryCode: string) {
        setSelectedName(name);
        setSelectedCountryCode(countryCode);
        onChange(scope);
    }

    return (
        <div className="flex items-center">
            <Label className="font-size-small pr-1">Location:</Label>

            <MenuTrigger>
                <WButton action={undefined}>
                    <span className="inline-flex items-center gap-1">
                        <CountryFlag code={selectedCountryCode} />
                        {selectedName}
                    </span>
                </WButton>
                <WPopover>
                    {treeStructure.map(({ country, locations }) => (
                        <div key={country.name}>
                            <WButton
                                variant="ghost"
                                className="w-full justify-start! pl-2!"
                                action={() =>
                                    onChangeScope({ type: "country", value: country }, country.name, country.code.iso3)
                                }
                            >
                                <LeftRight icon={<CountryFlag code={country.code.iso3} />} title={country.name} />
                            </WButton>

                            {locations.map(({ location, pools }) => (
                                <div key={location.display_name}>
                                    <WButton
                                        variant="ghost"
                                        action={() =>
                                            onChangeScope(
                                                { type: "location", value: location },
                                                `${location.display_name} (${location.slug})`,
                                                location.country_code,
                                            )
                                        }
                                        className="w-full justify-start! pl-6!"
                                    >
                                        <MaskIcon className="jp-web-icon size-0.875rem color-primary mr-1" />
                                        {location.display_name} ({location.slug})
                                    </WButton>

                                    {pools.map((pool) => (
                                        <div key={pool.uuid}>
                                            <WButton
                                                variant="ghost"
                                                action={() =>
                                                    onChangeScope(
                                                        { type: "pool", value: pool },
                                                        `${location.display_name} (${location.slug}), ${pool.name} `,
                                                        location.country_code,
                                                    )
                                                }
                                                className="w-full justify-start! pl-10!"
                                            >
                                                <MaskIcon className="jp-hostpool-icon size-0.875rem color-primary mr-1" />
                                                {pool.name}
                                            </WButton>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                </WPopover>
            </MenuTrigger>
        </div>
    );
}
